import { FC } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { FieldTableHeader } from 'components/_basic/Form/FieldTable2/components/FieldTableHeader';
import { FieldTableTotalRow } from 'components/_basic/Form/FieldTable2/components/FieldTableTotalRow';
import { FieldTableContainer } from 'components/_basic/Form/FieldTableContainer';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { IAssetsBody } from './types';
import { DividerComponent } from '../../../../../components/DividerComponent/DividerComponent';
import { TableSectionTitle } from '../../../../../components/TableComponents/TableSectionTitle';
import { assetsPrefix, FIRST_COLUMN_WIDTH } from '../../../../utils/form';
import { ASSETS_FORM_NAMES, useGetAssetsTotalRows } from '../../utils/form';
import { CurrentAssetsTable } from '../CurrentAssetsTable';
import { NonCurrentAssetsTable } from '../NonCurrentAssetsTable';

import styles from './Assets.module.scss';

export const AssetsBody: FC<IAssetsBody> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const columnsNames = getPeriodColumnsNames(period);
  const { totalAssets } = useGetAssetsTotalRows();

  const totalName = concatWithDot(assetsPrefix, ASSETS_FORM_NAMES.TOTAL_ASSETS);

  return (
    <FormSectionFilledCard>
      <FieldTableContainer>
        <Grid item xs className={styles.stickyTop}>
          <FieldTableHeader
            name={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
            disabled={disabled}
            columnsNames={columnsNames || []}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            editableColumn={true}
            firstColumnClass={styles.sticky}
          />
        </Grid>
        <Grid container direction="column" gap={4}>
          <Grid item xs>
            <Grid item xs>
              <TableSectionTitle title={t('financialPlan.projectedBalanceSheets.assets.currentAssets.title')} />
            </Grid>
            <Grid item xs>
              <CurrentAssetsTable disabled={disabled} />
            </Grid>
          </Grid>
          <Grid item xs>
            <Grid item xs>
              <TableSectionTitle title={t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.title')} />
            </Grid>
            <Grid item xs>
              <NonCurrentAssetsTable disabled={disabled} />
            </Grid>
          </Grid>
          <Grid item xs>
            <DividerComponent />
          </Grid>
          <Grid item xs>
            <FieldTableTotalRow
              totalName={totalName}
              columns={columnsNames || []}
              disabled={disabled}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={totalAssets}
            />
          </Grid>
        </Grid>
      </FieldTableContainer>
    </FormSectionFilledCard>
  );
};
